import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import EmployeePolicy, {
  EmployeeVerbTypes,
} from 'modules/core/store/models/policies/EmployeePolicy'

import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'

export const buildEmployee = (
  attributes: Partial<IEmployee> = {},
  permissions: Record<string, boolean | Record<string, boolean> | number> = {}
) => {
  return new Employee(buildEmployeeAttrs(attributes, permissions))
}

const eur = 'EUR'

export const buildEmployeeAttrs = (
  attributes: Partial<IEmployee> = {},
  permissions: Record<string, boolean | Record<string, boolean> | number> = {}
) => {
  return {
    access_id: 0,
    job_title: null,
    id: 0,
    manager_id: null,
    managed_by_current: false,
    supervised_by_current: false,
    fiscal_country: null,
    legal_entity_currency: eur,
    timeoff_policy_id: null,
    timeoff_manager_id: null,
    timeoff_supervised_by_current: false,
    tenure_start_date: null,
    location_id: 0,
    employee_group_id: null,
    payroll_hiring_id: 0,
    is_eligible_for_payroll: false,
    attendable: false,
    attendance_employees_policy_id: null,
    payment_frequency: null,
    contract_id: null,
    preferred_name: null,
    has_regular_access: false,
    regular_access_starts_on: '',
    irpf: 0,
    pronouns: null,
    permissions: {
      leaves: {
        read: false,
        create: false,
        approve: false,
        manage_documents: false,
        manage_incidences: false,
        update: false,
      },
      personal: {
        edit: false,
        edit_manager: false,
      },
      termination: false,
      profile: {
        email: {
          read: false,
        },
      },
      documents: {
        read: false,
        edit: false,
      },
      custom_tables: {
        create: false,
        remove: false,
      },
      payroll_supplements: {
        observations: {
          read: false,
        },
      },
      tasks: {
        read: false,
      },
      ...permissions,
    },
    country_metadata: {},
    terminated_on: null,
    is_terminating: false,
    termination_reason_type: null,
    termination_type_description: null,
    ...attributes,
  }
}

interface Basic {
  // Basic
  access_id: number
  job_title: string | null
  id: number
  manager_id: number | null
  managed_by_current: boolean
  supervised_by_current: boolean
  fiscal_country: string | null
  legal_entity_currency: string
  timeoff_policy_id: number | null
  timeoff_manager_id: number | null
  timeoff_supervised_by_current: boolean
  tenure_start_date: string | null
  location_id: number
  employee_group_id: number | null
  payroll_hiring_id: number
  is_eligible_for_payroll: boolean
  attendable: boolean
  attendance_employees_policy_id: number | null
  payment_frequency: string | null
  contract_id: number | null
  preferred_name: string | null
  has_regular_access: boolean
  regular_access_starts_on: string | null
  irpf: number | null
  pronouns: string | null
  permissions: BasicPermissions & ContextlessPolicies
}

interface ContextlessPolicies {
  [key: string]: any
}

interface BasicPermissions {
  leaves: {
    read: boolean
    create: boolean
    approve: boolean
    manage_documents: boolean
    manage_incidences: boolean
    update: boolean
  }
  personal: {
    edit: boolean
    edit_manager: boolean
  }
  termination: boolean
  profile: {
    email: {
      read: boolean
    }
  }
  documents: {
    read: boolean
    edit: boolean
  }
  custom_tables: {
    create: boolean
    remove: boolean
  }
  payroll_supplements: {
    observations: {
      read: boolean
    }
  }
  tasks: {
    read: boolean
  }
}

interface Admin {
  base_compensation_type: string | null
  base_compensation_amount_in_cents: number | null
  unfullfiled_pre_warning_days_by_company: number | null
  unfullfiled_pre_warning_days_by_employee: number | null
  company_identifier: string | null // DUPLICATE IN COMPANY IDENTIFIER
}

interface Manager {
  payrollable: boolean
}

interface Personal {
  country: string | null
  state: string | null
  postal_code: string | null
  city: string | null
  address_line_1: string | null
  address_line_2: string | null
  country_metadata: {
    [key: string]: any
  }
  contact_name: string | null
  contact_number: string | null
  personal_email: string | null
  phone_number: string | null
  hired_on: string | null // DEPRECATED
  identifier: string | null
  identifier_type: string | null
  gender: string | null
  nationality: string | null
  preferred_name: string | null // DUPLICATE
  birthday_on: string | null
  share_birthday: boolean | null // nilable in backend
  bank_number: string | null
  swift_bic: string | null
  bank_number_format: string | null
  disability_percentage_cents: number | null
  identifier_expiration_date: string | null
}

interface CompanyIdentifier {
  company_identifier?: string
}

interface Salary {
  base_compensation_type: string | null
  base_compensation_amount_in_cents: number | null
}

interface SeniorityCalculationDate {
  seniority_calculation_date: string | null
}

interface Termination {
  terminated_on: string | null
  is_terminating: boolean
  termination_reason_type: string | null
  termination_type_description: string | null
}

interface PreferredName {
  preferred_name: string | null
}

export type IEmployee = Basic &
  Partial<Admin> &
  Partial<Manager> &
  Partial<Personal> &
  Partial<CompanyIdentifier> &
  Partial<Salary> &
  Partial<SeniorityCalculationDate> &
  Termination &
  Partial<PreferredName>

class Employee extends ModelWithPolicy<IEmployee, EmployeeVerbTypes> implements PolicyTarget {
  static urlRoot() {
    return '/employees'
  }

  static PAYMENT_FREQUENCIES: { [key: string]: string } = {
    monthly: 'monthly',
    semimonthly: 'semimonthly',
    weekly: 'weekly',
  }

  static FAMILY_SITUATION: { [key: string]: string } = {
    single: 'single',
    cohabitating: 'cohabitating',
    divorced: 'divorced',
    married: 'married',
    unknown: 'unknown',
    civilPartnership: 'civil_partnership',
    separated: 'separated',
    widow: 'widow',
    notApplicable: 'not_applicable',
  }

  static DEFAULT_GROSS_SALARY = 2700000
  static ACCESS_TYPE: { [key: string]: string } = {
    welcome_space: 'welcome_space',
    regular: 'regular',
  }

  static TERMINATION_REASON_TYPE: { [key: string]: string } = {
    company: 'company',
    employee: 'employee',
    others: 'others',
  }

  static policyClass = '::Employee'

  constructor(attr: IEmployee) {
    super(attr)
    makeObservable(this, {
      timeoffManager: computed,
      manager: computed,
      isTerminated: computed,
      terminationDate: computed,
      isTerminating: computed,
      isEligibleForPayroll: computed,
      seniorityDate: computed,
    })
  }

  get seniorityDate() {
    const referenceSeniorityDate =
      this.attributes.seniority_calculation_date || this.attributes.tenure_start_date
    return referenceSeniorityDate && stringToDate(referenceSeniorityDate, 'backend')
  }

  get policy() {
    return new EmployeePolicy(this)
  }

  get policyTargetClass(): string {
    return Employee.policyClass
  }

  get timeoffManager(): Employee | null | undefined {
    const timeoffManagerId = this.attributes.timeoff_manager_id

    if (!timeoffManagerId) return null

    return this.collection?.get(timeoffManagerId)
  }

  get manager(): Employee | null | undefined {
    const managerId = this.attributes.manager_id

    if (!managerId) return null

    return this.collection?.get(managerId)
  }

  /**
   * @deprecated
   */
  isSupervisedByCurrent(): boolean {
    return !!this.attributes.supervised_by_current
  }

  /**
   * @deprecated
   */
  isManagedByCurrent(): boolean {
    return !!this.attributes.managed_by_current
  }

  get isTerminated(): boolean {
    if (!this.attributes.terminated_on) return false

    return stringToDate(this.attributes.terminated_on, 'backend').isBefore(moment(), 'day')
  }

  get terminationDate(): moment.Moment | null | undefined {
    const termDate = this.attributes.terminated_on

    if (!termDate) return null

    return stringToDate(termDate, 'backend')
  }

  isActiveOn(date: moment.Moment): boolean {
    const terminationDate = this.terminationDate

    if (!terminationDate) return true

    return terminationDate.isSameOrAfter(date, 'day')
  }

  activeWithinDateRange(startDate: moment.Moment, endDate: moment.Moment): boolean {
    const terminationDate = this.terminationDate

    if (!terminationDate) return true

    if (this.isActiveOn(startDate)) return true

    return (
      terminationDate.isSameOrBefore(endDate, 'day') &&
      terminationDate.isSameOrAfter(startDate, 'day')
    )
  }

  hasRegularAccess(isEmployeeOnboarding: boolean): boolean {
    if (!isEmployeeOnboarding) return true

    return this.attributes.has_regular_access
  }

  get isTerminating(): boolean {
    return this.attributes.is_terminating
  }

  get isEligibleForPayroll(): boolean {
    return this.attributes.is_eligible_for_payroll
  }

  setRegularAccessStartDate(startsOn?: moment.Moment): Promise<any> {
    return this.rpc('set_regular_access_start_date', {
      starts_on: startsOn,
    })
  }
}

export default Employee
